/** Minimal horizontal wave shader — solid base + soft glowing band. */

export const WAVE_VERT = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const WAVE_FRAG = /* glsl */ `
precision highp float;

varying vec2 vUv;

uniform float uTime;
uniform vec2 uResolution;
uniform vec3 uBase;
uniform vec3 uWave;
uniform vec3 uWaveViolet;
uniform vec3 uWaveSunset;
uniform float uBandCenter;
uniform float uCenterFloor;
uniform float uContentDim;
uniform float uIsLight;
uniform float uSplashBloom;

/* Vertical displacement only — mean is always zero so lines stay centred on uBandCenter */
float waveOffset(float x, float t) {
  return sin(x * 1.0 + t * 0.42) * 0.022
       + sin(x * 1.65 + t * 0.28 + 1.2) * 0.011
       + sin(x * 2.4 + t * 0.18 + 2.4) * 0.006;
}

/* Traveling shimmer — wide luminance swing + sweeping bright sparks */
float waveShimmer(float px, float t, float linePhase) {
  float along = sin(px * 0.78 + t * 0.82 + linePhase) * 0.5 + 0.5;
  float ripple = sin(px * 2.8 + t * 1.45 + linePhase * 1.35) * 0.5 + 0.5;
  float breathe = sin(t * 0.42 + linePhase * 0.75 + px * 0.09) * 0.5 + 0.5;
  float spark = pow(sin(px * 1.15 + t * 1.35 + linePhase * 0.55) * 0.5 + 0.5, 2.0);
  float mixVal = along * 0.34 + ripple * 0.26 + breathe * 0.16 + spark * 0.24;
  return 0.52 + 0.68 * mixVal;
}

float waveHueShift(float px, float t, float linePhase) {
  return sin(px * 1.05 + t * 1.05 + linePhase * 1.1) * 0.5 + 0.5;
}

void main() {
  vec2 uv = vUv;
  vec3 col = uBase;

  float aspect = uResolution.x / max(uResolution.y, 1.0);
  float x = uv.x * aspect * 6.283;
  float t = uTime * 0.35;
  float drift = t * 0.4;
  float px = x + drift;

  /* Three lines locked symmetrically around viewport vertical centre */
  float spread = 0.028;
  float y0 = uBandCenter + waveOffset(px, t) - spread;
  float y1 = uBandCenter + waveOffset(px + 0.6, t * 1.05);
  float y2 = uBandCenter + waveOffset(px + 1.2, t * 0.95) + spread;

  float d0 = abs(uv.y - y0);
  float d1 = abs(uv.y - y1);
  float d2 = abs(uv.y - y2);
  float dist = min(d0, min(d1, d2));

  /* Per-line shimmer blended by proximity — each line drifts slightly out of phase */
  float w0 = exp(-d0 * 72.0);
  float w1 = exp(-d1 * 72.0);
  float w2 = exp(-d2 * 72.0);
  float wSum = w0 + w1 + w2 + 1e-4;
  float shimmer =
    (w0 * waveShimmer(px, t, 0.0)
   + w1 * waveShimmer(px + 0.6, t * 1.05, 1.25)
   + w2 * waveShimmer(px + 1.2, t * 0.95, 2.55)) / wSum;
  float hue =
    (w0 * waveHueShift(px, t, 0.0)
   + w1 * waveHueShift(px + 0.6, t * 1.05, 1.25)
   + w2 * waveHueShift(px + 1.2, t * 0.95, 2.55)) / wSum;

  /* Ribbon shape — light mode uses a softer, wider glow.
     Splash bloom widens the falloff and adds a haze veil behind the logo. */
  float bloom = clamp(uSplashBloom, 0.0, 1.0);
  float peak = smoothstep(0.42, 0.98, shimmer);
  float trough = smoothstep(0.88, 0.38, shimmer);
  float core;
  float glow;
  float haze;
  if (uIsLight > 0.5) {
    core = exp(-dist * mix(88.0, 80.0, bloom)) * mix(0.22, 0.44, peak) * mix(1.0, 1.03, bloom);
    glow = exp(-dist * mix(22.0, 17.0, bloom)) * mix(0.08, 0.16, shimmer) * mix(1.0, 1.14, bloom);
    haze = exp(-dist * mix(14.0, 10.0, bloom)) * mix(0.0, 0.05, bloom) * mix(0.75, 1.05, shimmer);
  } else {
    core = exp(-dist * mix(120.0, 108.0, bloom)) * mix(0.42, 0.78, peak) * mix(1.0, 1.04, bloom);
    glow = exp(-dist * mix(32.0, 24.0, bloom)) * mix(0.16, 0.34, shimmer) * mix(1.0, 1.16, bloom);
    haze = exp(-dist * mix(16.0, 11.0, bloom)) * mix(0.0, 0.07, bloom) * mix(0.7, 1.06, shimmer);
  }
  float band = core + glow + haze;

  /* Dim card area so content stays readable — independent of wave position */
  vec2 center = vec2(0.5, 0.4);
  vec2 ell = (uv - center) / vec2(0.44, 0.34);
  float spatial = mix(uCenterFloor, 1.0, smoothstep(0.15, 0.85, length(ell)));
  spatial = mix(spatial, 1.0, bloom * 0.18);

  vec3 waveRgb;
  float brightMul;
  float corePulse;

  if (uIsLight > 0.5) {
    /* Light: violet troughs → orchid mid → sunset coral/peach peaks */
    vec3 waveDeep = mix(uWaveViolet, uWaveViolet * vec3(0.92, 0.86, 1.08), 0.35);
    vec3 waveMid = mix(uWaveViolet, uWave, hue * 0.45 + 0.25);
    vec3 waveWarm = mix(uWave, uWaveSunset, 0.62);
    vec3 wavePeak = mix(
      uWaveSunset,
      min(uWaveSunset * vec3(1.06, 1.04, 0.98) + vec3(0.08, 0.05, 0.02), vec3(1.0)),
      hue
    );
    waveRgb = mix(waveMid, waveDeep, trough * 0.58);
    waveRgb = mix(waveRgb, waveWarm, peak * 0.48);
    waveRgb = mix(waveRgb, wavePeak, peak * peak * 0.78);
    brightMul = mix(0.76, 1.26, shimmer) * mix(1.0, 1.03, bloom);
    corePulse = 1.0 + 0.28 * peak * core;
  } else {
    /* Dark: vivid neon swing — muted troughs → cyan → ice peaks */
    vec3 waveDeep = uWave * vec3(0.42, 0.52, 0.68);
    vec3 waveVivid = uWave * vec3(0.92, 1.38, 1.62);
    vec3 waveIce = min(uWave + vec3(0.42, 0.52, 0.62), vec3(1.0));
    waveRgb = mix(uWave, waveDeep, trough * 0.72);
    waveRgb = mix(waveRgb, mix(waveVivid, waveIce, hue), peak);
    brightMul = mix(0.58, 1.72, shimmer) * mix(1.0, 1.04, bloom);
    corePulse = 1.0 + 0.65 * peak * core;
  }

  float waveStrength = band * brightMul * spatial * corePulse * uContentDim * mix(1.0, 1.05, bloom);

  if (uIsLight > 0.5) {
    /* Mix tint into base — enough alpha for violet/sunset to read on pale UI */
    float waveAlpha = clamp(waveStrength * mix(0.72, 0.76, bloom), 0.0, mix(0.65, 0.68, bloom));
    col = mix(col, waveRgb, waveAlpha);
  } else {
    col += waveRgb * waveStrength;
  }

  gl_FragColor = vec4(col, 1.0);
}
`;
